/*
 * Purpose: JSON-LD structured data — Organization, WebSite, SoftwareApplication, FAQ
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { COMPANY_INFO } from '@/lib/companyInfo';
import { getSeoForPath, SEO_SITE } from '@/lib/seoMeta';

const SCRIPT_ID = 'autoblocks-json-ld';

function buildGraph(pathname: string) {
  const seo = getSeoForPath(pathname);
  const graph: object[] = [
    {
      '@type': 'Organization',
      '@id': `${SEO_SITE.url}/#organization`,
      name: COMPANY_INFO.legalName,
      legalName: COMPANY_INFO.legalName,
      url: SEO_SITE.url,
      logo: `${SEO_SITE.url}/brand-mark.svg`,
      sameAs: [COMPANY_INFO.marketingUrl, COMPANY_INFO.repository],
    },
    {
      '@type': 'WebSite',
      '@id': `${SEO_SITE.url}/#website`,
      url: SEO_SITE.url,
      name: SEO_SITE.name,
      description: seo.description,
      publisher: { '@id': `${SEO_SITE.url}/#organization` },
      inLanguage: 'en-GB',
    },
  ];

  if (pathname === '/' || pathname === '') {
    graph.push({
      '@type': 'SoftwareApplication',
      '@id': `${SEO_SITE.url}/#software`,
      name: SEO_SITE.name,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Outcome-driven workflow automation platform — Zapier, Make, Integromat, and n8n alternative with owned connectors, dry-run, and human approval.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'GBP',
        description: 'Product app — see autoblocks.pro for pricing',
      },
      featureList: [
        'AutoBlock Flow Composer',
        'Owned connector library',
        'Dry-run before live',
        'Gmail inbound triggers',
        'Mission map and monitor',
        'Zapier Make n8n alternative',
      ],
      provider: { '@id': `${SEO_SITE.url}/#organization` },
    });

    if (seo.faq?.length) {
      graph.push({
        '@type': 'FAQPage',
        '@id': `${SEO_SITE.url}/#faq`,
        mainEntity: seo.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      });
    }
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

export function JsonLdScript() {
  const { pathname } = useLocation();

  useEffect(() => {
    const payload = buildGraph(pathname);
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(payload);
  }, [pathname]);

  return null;
}

export default JsonLdScript;
